<template>
  <v-container class="pa-0" fluid>
    <v-layout fluid>
      <v-flex xs12>
        <v-layout row>
          <v-spacer></v-spacer>
          <v-tooltip
            bottom
            v-if="
              showNewPaymentButton &&
              !$store.getters.hasAccessTo('finances:pi_payments_view_only')
            "
          >
            <template v-slot:activator="{ on }">
              <v-btn
                class="mr-3"
                v-on="on"
                small
                fab
                dark
                color="primary"
                @click="createPayment"
              >
                <v-icon dark>add</v-icon>
              </v-btn>
            </template>
            <span>Create new payment</span>
          </v-tooltip>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="payments"
      v-bind="pagingOptions"
      item-key="id"
      fluid
    >
      <template v-slot:item.paymentDate="{ item }">{{
        item.paymentDate | formatDate
      }}</template>
      <template v-slot:item.amount="{ item }">{{
        item.amount | priceLabeler
      }}</template>
      <template v-slot:item.actions="{ item }">
        <default-list-actions
          :item="item"
          :hide-view="true"
          :hide-edit="
            $store.getters.hasAccessTo('finances:pi_payments_view_only')
          "
          :hide-remove="
            $store.getters.hasAccessTo('finances:pi_payments_view_only')
          "
          @deleteItem="onDeletePayment"
          @editItem="onEditPayment"
        ></default-list-actions>
      </template>
    </v-data-table>

    <v-dialog v-model="showPaymentEditorDialog" persistent max-width="1000">
      <v-card>
        <v-card-title class="grey lighten-3">
          <v-row>
            <v-icon class="draggable">mdi-drag</v-icon>
            <v-col class="py-0">Create Purchase Invoice Payment</v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pb-0 pt-3">
          <purchase-invoice-payment-details-editor
            @onChange="payment = $event"
            :payment-dialog="showPaymentEditorDialog"
            :purchase-invoice-payment="payment"
          ></purchase-invoice-payment-details-editor>
        </v-card-text>
        <v-card-actions class="grey lighten-3">
          <v-spacer></v-spacer>

          <v-btn color="warning" text @click="closeDialog">Cancel</v-btn>
          <v-btn text v-if="showXero" @click="onExportToXero"
            >Export to XERO</v-btn
          >
          <v-btn v-if="showQuickBooks" @click="onExportToQuickBooks"
            >Export to Quick Books</v-btn
          >
          <v-btn @click="savePayment(false)" :disabled="!isValid"
            >Save and Continue</v-btn
          >
          <v-btn color="primary" @click="savePayment(true)" :disabled="!isValid"
            >Save and Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div v-if="debug">
      {{ payments }}
    </div>
  </v-container>
</template>

<script>
import PurchaseInvoicePaymentDetailsEditor from "../purchase-invoice-payments/PurchaseInvoicePaymentDetailsEditor";
import DefaultListActions from "../../shared/DefaultListActions";
import { sortByDate, pagingOptions } from "../../../../helpers/index";
import {
  EXPORT_PAYMENT_TO_XERO_MUTATION,
  EXPORT_PAYMENT_TO_QUICK_BOOKS_MUTATION,
} from "../../../../constants/graphql";
import EventBus from "../../../../store/event-bus";
export default {
  name: "PurchaseInvoiceDetailsPaymentList",

  components: {
    PurchaseInvoicePaymentDetailsEditor,
    DefaultListActions,
  },

  props: {
    payments: {
      type: Array,
      default: () => [],
    },
    supplierName: {
      type: String,
      default: "",
    },
    itineraryReference: {
      type: String,
      default: "",
    },
    balance: {
      type: Number,
      default: () => 0,
    },
    currencyCode: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      debug: false,
      pagingOptions,
      showPaymentEditorDialog: false,
      payment: {
        supplierName: this.supplierName,
        itineraryReference: this.itineraryReference,
        amount: this.balance,
        currencyCode: this.currencyCode,
        paymentDate: new Date().toISOString(true),
        type: "",
      },
      headers: [
        {
          text: "Amount",
          align: "left",
          value: "amount",
          name: "amount",
        },
        {
          text: "Currency",
          align: "left",
          value: "currencyCode",
          name: "currencyCode",
        },
        {
          text: "Exchange Rate",
          align: "left",
          value: "exchangeRate",
          name: "exchangeRate",
        },
        {
          text: "Type",
          align: "left",
          value: "paymentType",
          name: "paymentType",
        },
        {
          text: "Ref",
          align: "left",
          value: "reference",
          name: "reference",
        },
        {
          text: "Date",
          align: "left",
          value: "paymentDate",
          name: "paymentDate",
          sort: sortByDate,
        },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  computed: {
    showXero() {
      return (
        this.$store.getters.hasAccessTo("finances:xero_export") &&
        this.payment.id
      );
    },
    showQuickBooks() {
      return (
        this.$store.getters.hasAccessTo("finances:quick_books") &&
        this.payment.id
      );
    },
    showNewPaymentButton() {
      return this.$store.getters.hasAccessTo("finances:pi_payments");
    },

    isValid() {
      return this.payment.type && this.payment.type != "";
    },
  },

  watch: {
    balance(newVal) {
      this.payment.amount = newVal;
    },
  },

  methods: {
    onExportToQuickBooks() {
      this.$apollo
        .mutate({
          mutation: EXPORT_PAYMENT_TO_QUICK_BOOKS_MUTATION,
          variables: { id: this.payment.id },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportPaymentToQuickBooks.message,
            color: data.exportPaymentToQuickBooks.success ? "success" : "error",
          });
        })
        .catch((err) => {
          console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onExportToXero() {
      console.log("exporting", this.payment.id);
      this.$apollo
        .mutate({
          mutation: EXPORT_PAYMENT_TO_XERO_MUTATION,
          variables: {
            input: { id: this.payment.id },
          },
        })
        .then(({ data }) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: data.exportPaymentToXero.message,
            color: data.exportPaymentToXero.status,
          });
        })
        .catch((err) => {
          console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    savePayment(close = false) {
      this.$emit("onChange", {
        action: this.payment.id ? "edit" : "create",
        payload: { ...this.payment },
      });
      if (close) {
        this.closeDialog();
      }
    },
    onDeletePayment(item) {
      this.$emit("onChange", {
        action: "delete",
        payload: { ...item },
      });
    },
    createPayment() {
      this.showPaymentEditorDialog = true;
    },
    onEditPayment(item) {
      this.payment = { ...item, type: item.paymentType };
      this.showPaymentEditorDialog = true;
    },
    closeDialog() {
      this.showPaymentEditorDialog = false;
      this.payment = {
        supplierName: this.supplierName,
        itineraryReference: this.itineraryReference,
        amount: this.balance,
        currencyCode: this.currencyCode,
        paymentDate: new Date().toISOString(true),
      };
    },
    onPiPaymentSaved(payload) {
      if (this.showPaymentEditorDialog) {
        this.payment = { ...this.payment, id: payload.id };
      }
    },
  },
  mounted() {
    EventBus.$on("piPaymentSaved", this.onPiPaymentSaved);
  },
  beforeDestroy() {
    EventBus.$off("piPaymentSaved", this.onPiPaymentSaved);
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/finances/purchase-invoices/PurchaseInvoiceDetailsPaymentList.vue